/**
 * Job List API Contracts
 *
 * ts-rest contract definitions for the paginated job list endpoints used by works-lite.
 *
 * Endpoints :
 *   GET /jobs/job-list          — base filters
 *   GET /jobs/job-list-extended — extended filters (status, assignee, date-range, sort)
 * Auth      : Required (standard bearer token via middleware)
 * Response  : Paginated list of jobs with total count
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

/**
 * Zod schema for a single job list item
 */
export const jobListItemSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  job_category_id: z.number().nullable(),
  status: z.number().nullable(),
  priority: z.string().nullable(),
  priority_id: z.number().nullable(),
  priority_name: z.string().nullable(),
  address: z.string().nullable(),
  req_completion_date: z.string().nullable(),
  created_time: z.string().nullable(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  work_order_status_id: z.number().nullable(),
  work_order_status: z.string().nullable(),
  permit_required: z.boolean(),
  reinstatement_required: z.boolean(),
  permit_status_id: z.number().nullable(),
  permit_status_name: z.string().nullable(),
  job_category_name: z.string().nullable(),
  mir_pending_direction: z.enum(['inbound', 'outbound']).nullable(),
  external_id: z.string().nullable(),
});

/**
 * Zod schema for the paginated job list response
 */
export const jobListResponseSchema = z.object({
  data: z.array(jobListItemSchema),
  total: z.number(),
  limit: z.number(),
  skip: z.number(),
});

/**
 * Zod schema for query parameters
 */
const toArray = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(
    (val) => (Array.isArray(val) ? val : val != null ? [val] : undefined),
    z.array(schema),
  );

export const jobListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  skip: z.coerce.number().int().min(0).default(0),
  priority_id: toArray(z.coerce.number().int()).optional(),
  job_category_id: toArray(z.coerce.number().int()).optional(),
  job_category_name: toArray(z.string()).optional(),
  work_order_status_id: toArray(z.coerce.number().int()).optional(),
  work_order_status_name: toArray(z.string()).optional(),
  search_term: z.string().optional(),
  mir_pending_direction: z.enum(['inbound', 'outbound', 'none']).optional(),
});

export const jobListExtendedQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  skip: z.coerce.number().int().min(0).default(0),
  priority_id: toArray(z.coerce.number().int()).optional(),
  job_category_id: toArray(z.coerce.number().int()).optional(),
  job_category_name: toArray(z.string()).optional(),
  work_order_status_id: toArray(z.coerce.number().int()).optional(),
  work_order_status_name: toArray(z.string()).optional(),
  status: toArray(z.coerce.number().int()).optional(),
  assignee_user_id: toArray(z.coerce.number().int()).optional(),
  assignee_team_id: toArray(z.coerce.number().int()).optional(),
  created_from: z.coerce.date().optional(),
  created_to: z.coerce.date().optional(),
  updated_from: z.coerce.date().optional(),
  updated_to: z.coerce.date().optional(),
  due_from: z.coerce.date().optional(),
  due_to: z.coerce.date().optional(),
  search_term: z.string().optional(),
  mir_pending_direction: z.enum(['inbound', 'outbound', 'none']).optional(),
  sort_by: z
    .enum([
      'id',
      'name',
      'priority_id',
      'status',
      'work_order_status_id',
      'job_category_id',
      'req_completion_date',
      'created_time',
      'updated_time',
      'start_date',
      'end_date',
    ])
    .optional(),
  sort_order: z.enum(['asc', 'desc']).optional(),
});

/**
 * TypeScript types inferred from Zod schemas
 */
export type JobListItem = z.infer<typeof jobListItemSchema>;
export type JobListResponse = z.infer<typeof jobListResponseSchema>;
export type JobListQuery = z.infer<typeof jobListQuerySchema>;
export type JobListExtendedQuery = z.infer<typeof jobListExtendedQuerySchema>;

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

export const jobListContract = c.router({
  getJobList: {
    method: 'GET',
    path: '/',
    query: jobListQuerySchema,
    responses: {
      200: jobListResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get a paginated list of active jobs',
    description:
      'Returns jobs where is_delete = false, ordered by created_time DESC. Supports limit/skip pagination.',
  },
});

export const jobListExtendedContract = c.router({
  getJobListExtended: {
    method: 'GET',
    path: '/',
    query: jobListExtendedQuerySchema,
    responses: {
      200: jobListResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get a paginated list of active jobs with extended filters',
    description:
      'Returns jobs where is_delete = false. Supports status, assignee (user/team), date-range, sort and expanded search filters.',
  },
});
